import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, IndianRupee, Search, TrendingUp, Wallet } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SummaryCard } from '@/components/SummaryCard';
import { DataTable, nextSortDirection, type SortDirection } from '@/components/DataTable';
import { EMPTY_MONTH_RANGE, type MonthRange } from '@/components/ColumnFilter';
import { useLoansQuery } from '@/api/query/loan';
import { useAppContext } from '@/contexts/AppContext';
import { useDrawers } from '@/contexts/DrawerContext';
import type { Loan } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useTableFirstScroll } from '@/hooks/useTableFirstScroll';
import { createColumns } from './TableColumns';

type LoanFilter = 'all' | 'active' | 'due' | 'closed';

const FILTERS: { id: LoanFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'due', label: 'Due This Month' },
  { id: 'closed', label: 'Closed' },
];

const matchesFilter = (loan: Loan, filter: LoanFilter) => {
  if (filter === 'active') return loan.status === 'active';
  if (filter === 'due') return loan.status === 'active' && loan.thisMonth === 'unpaid';
  if (filter === 'closed') return loan.status === 'closed';
  return true;
};

export default function LoanManagement() {
  const { pageRef, tableLocked } = useTableFirstScroll();
  const { isAuthenticated } = useAppContext();
  const { openDrawer } = useDrawers();

  const { searchTerm, setSearchTerm, filter, setFilter, page, setPage, pageSize, setPageSize, pageQuery } =
    useTableQuery<LoanFilter>('all');
  const { data, isLoading, isFetching, error } = useLoansQuery(pageQuery, isAuthenticated);
  const totalLoans = data?.total ?? 0;
  // The backend searches; the filter applies to the loans of the current page.
  // ID sort: null = the order the API sends (also after a reload).
  const [idSort, setIdSort] = useState<SortDirection | null>(null);
  // This Month and Given On header choices: UI only for now, the rows aren't filtered by them.
  const [thisMonth, setThisMonth] = useState('');
  const [givenOn, setGivenOn] = useState<MonthRange>(EMPTY_MONTH_RANGE);
  // The filter buttons and ID sort apply to the rows of the current page.
  const loans = useMemo(() => {
    const rows = (data?.loans ?? []).filter((loan) => matchesFilter(loan, filter));
    if (!idSort) return rows;
    const direction = idSort === 'asc' ? 1 : -1;
    return rows.sort((a, b) => a.memberId.localeCompare(b.memberId, undefined, { numeric: true }) * direction);
  }, [data, filter, idSort]);
  const isNarrowed = filter !== 'all';
  const overview = data?.overview;


  const columns = useMemo(
    () =>
      createColumns({
        onOpenLoan: (loan) => openDrawer('policyLoan', { id: loan.memberId, name: loan.memberName, memberNo: loan.memberNo }, loan.policyId),
        thisMonthFilter: { value: thisMonth, onChange: setThisMonth },
        givenOnFilter: { range: givenOn, onChange: setGivenOn },
      }),
    [openDrawer, thisMonth, givenOn]
  );
  const show = (value: string) => (isLoading ? '--' : value);

  return (
    <div ref={pageRef} data-table-locked={tableLocked} className="group/page flex h-full flex-col overflow-y-auto p-5">
      <div className="mb-6 shrink-0">
        <div className="mb-4 flex items-center gap-2">
          <h1 className="text-lg font-bold text-gray-900">Loan Management</h1>
          {isFetching && !isLoading && (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#4369a0]/25 border-t-[#4369a0]" />
          )}
        </div>
        <p className="-mt-3 mb-4 text-sm text-gray-500">Track policy loans, repayments and monthly dues</p>
        <div className="h-px bg-gray-200/80" />
      </div>

      <div className="mb-5 grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <SummaryCard
          icon={Wallet}
          tone="brand"
          label="Active Loans"
          value={show(String(overview?.active ?? 0))}
          hint={`${formatCurrency(overview?.activeGiven ?? 0)} given · ${overview?.activeMembers ?? 0} members`}
        />
        <SummaryCard
          icon={IndianRupee}
          tone="gray"
          label="Outstanding"
          value={show(formatCurrency(overview?.outstanding ?? 0))}
          hint={`${formatCurrency(overview?.principalRepaid ?? 0)} principal repaid`}
        />
        <SummaryCard
          icon={AlertCircle}
          tone="amber"
          label="Due This Month"
          value={show(String(overview?.dueThisMonth ?? 0))}
          hint={`${formatCurrency(overview?.dueAmount ?? 0)} due · ${overview?.paidThisMonth ?? 0} paid`}
        />
        <SummaryCard
          icon={TrendingUp}
          tone="teal"
          label="Interest Earned"
          value={show(formatCurrency(overview?.interestEarned ?? 0))}
          hint="9% per year on outstanding"
        />
        <SummaryCard
          icon={CheckCircle2}
          tone="emerald"
          label="Closed Loans"
          value={show(String(overview?.closed ?? 0))}
          hint={`${formatCurrency(overview?.closedRepaid ?? 0)} fully repaid`}
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Unable to load loans. Please try again.
        </div>
      )}

      <Card className="flex h-full shrink-0 flex-col border-gray-200/80 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-5">
          <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
            <h2 className="whitespace-nowrap text-sm font-semibold text-gray-900">
              Loans
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {isNarrowed ? loans.length : totalLoans}
              </span>
            </h2>

            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by ID, card no, name, phone, loan no or policy no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 border-gray-200 bg-white pl-9"
              />
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
              {FILTERS.map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilter(item.id)}
                  className={`h-8 px-3 text-xs ${
                    filter === item.id
                      ? 'bg-[#4369a0]/10 font-medium text-[#4369a0] hover:bg-[#4369a0]/15'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-gray-200/80 bg-white">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                <p className="mt-3 text-sm text-gray-500">Loading loans...</p>
              </div>
            ) : (
              <DataTable
                items={loans}
                columns={columns}
                getKey={(loan) => loan.id}
                rowClassName={(loan) => (loan.status === 'closed' ? 'bg-gray-50/60 text-gray-500' : undefined)}
                emptyIcon={Wallet}
                emptyTitle="No loans found"
                itemLabel="loans"
                currentPage={page}
                pageSize={pageSize}
                totalItems={totalLoans}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                firstColumnSort={{ direction: idSort, onToggle: () => setIdSort(nextSortDirection) }}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
